import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { HelpCircle } from 'lucide-react';
import { Button } from './ui/button';

const FAQSection = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const faqs = [
    {
      question: 'What age group are your reading programmes for?',
      answer: 'Our "Learn to Read" programmes are designed for children aged 3 to 6 years old. We focus on early literacy, phonics, sound awareness, blending, and reading readiness — preparing children for confident, lifelong reading.'
    },
    {
      question: 'Are your classes online or in-person?',
      answer: 'We primarily offer online classes that are accessible across different time zones worldwide, giving children flexible learning regardless of location. Each session is interactive, engaging, and child-friendly.'
    },
    {
      question: 'How is Read Right Academy different from regular tuition?',
      answer: 'We don\'t rush children into reading. Our philosophy is to prepare the child first — building sound awareness, confidence, listening skills, and vocabulary before reading becomes natural. We use structured phonics with patience and joy, not pressure.'
    },
    {
      question: 'Do I need to book a trial before enrolling?',
      answer: 'We strongly recommend the Free Trial Class. It is a gentle 45-minute observation — not a formal assessment — that helps us understand your child\'s reading readiness, learning needs, and recommend the most suitable programme, whether One-to-One or Read Together.'
    },
    {
      question: 'I\'m a parent. Can I learn how to teach my child at home?',
      answer: 'Absolutely! We offer dedicated programmes for parents: the Parent Educator Programme (overall home learning support) and the Parent Phonics Guide (practical phonics support). Both equip you with the knowledge and confidence to support your child at home.'
    },
    {
      question: 'I\'m a teacher or aspiring educator. Do you offer training?',
      answer: 'Yes! Our Phonics Teacher Training programme is designed for teachers, assistant teachers, homeschool educators, and aspiring educators. You\'ll learn structured phonics methodology, lesson planning, and child-friendly teaching strategies.'
    },
    {
      question: 'How long does it take for a child to start reading?',
      answer: 'Every child learns at their own pace. With consistent practice and our structured approach, most children begin building strong reading foundations within a few months. Our focus is steady, confident progress — not speed.'
    },
    {
      question: 'How can I get started?',
      answer: 'Simply book a Free Trial Class through our contact form, WhatsApp us at +60 16-629 1687, or email teacherkalah@gmail.com. We\'ll guide you to the best programme for your child or your learning needs.'
    }
  ];

  return (
    <section id="faq" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full mb-6">
            <HelpCircle className="h-8 w-8 text-[#3B4BA0]" />
          </div>
          <div className="inline-block bg-blue-50 text-[#3B4BA0] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Frequently Asked Questions
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Common Questions Answered
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Everything you need to know about Read Right Academy programmes, 
            classes, and how we support children, parents, and educators.
          </p>
        </div>

        {/* FAQ Accordion */}
        <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
          {faqs.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              data-testid={`faq-item-${index}`}
              className="bg-white border-2 border-gray-100 hover:border-[#3B4BA0] rounded-xl px-6 transition-all duration-300 data-[state=open]:border-[#3B4BA0] data-[state=open]:shadow-lg"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-[#3B4BA0] hover:no-underline py-5">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed pb-5">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Still have questions */}
        <div className="mt-12 text-center bg-gradient-to-r from-[#3B4BA0] to-teal-600 rounded-2xl p-10 text-white">
          <h3 className="text-2xl sm:text-3xl font-bold mb-4">
            Still Have Questions?
          </h3>
          <p className="text-blue-50 mb-6 max-w-2xl mx-auto">
            We're here to help. Reach out and we'll guide you to the best programme 
            for your child or your learning journey.
          </p>
          <Button
            size="lg"
            onClick={scrollToContact}
            data-testid="faq-contact-cta"
            className="bg-white text-[#3B4BA0] hover:bg-gray-100 text-lg px-10 py-6 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Get In Touch
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
